'use client'

import { useState, useEffect, useCallback } from 'react'
import { useSession } from 'next-auth/react'
import apiClient from '@/lib/noc-api-client'
import { useCircuitBreaker } from '@/contexts/CircuitBreakerContext'
import { socApi, suspensionApi, type SuspensionEvent } from '@/lib/soc-api-client'
import { Spinner } from '@/components/ui/spinner'
import { Button } from '@/components/ui/button'
import { Download, ChevronDown } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import toast from 'react-hot-toast'
import { useLanguage } from '@/contexts/LanguageContext'
import { useWebhookEvents, type WebhookEvent } from '@/hooks/useWebhookEvents'

// Import widgets
import { KpiStats } from '@/components/dashboard/KpiStats'
import { NetworkHealthList } from '@/components/dashboard/NetworkHealthList'
import { GlobalHealthDonut, type GlobalHealthData } from '@/components/dashboard/GlobalHealthDonut'
import { ResourceMonitoringWidget } from '@/components/dashboard/ResourceMonitoringWidget'
import { OrganizationMetrics } from '@/components/dashboard/OrganizationMetrics'
import { ClientTopTalkersWidget } from '@/components/dashboard/ClientTopTalkersWidget'
import { DeviceFleetWidget } from '@/components/dashboard/DeviceFleetWidget'
import { AskNocManager } from '@/components/dashboard/AskNocManager'
import { NocAgentStatusCard } from '@/components/dashboard/NocAgentStatusCard'
// Define types for dashboard data
interface GlobalHealth {
  counts: {
    online: number;
    alerting: number;
    offline: number;
    dormant?: number;
  };
  total: number;
}

interface AlertSummaryData {
  status: 'active' | 'all_clear' | 'no_alerts';
  high: number;
  medium: number;
  resolved: number;
  top_alert_types: string[];
  dominant_network?: string;
}

interface ActiveIncidents {
  high?: number;
  medium?: number;
  resolved?: number;
  alert_summary?: string;
  alert_summary_data?: AlertSummaryData;
  by_network?: Record<string, {
    name: string;
    high: number;
    medium: number;
    resolved: number;
  }>;
  list?: Array<{
    id: string;
    title: string;
    status?: string;
    priority?: string;
    recommended_action_type?: string;
    network_id?: string;
  }>;
}

interface Network {
  id: string;
  name: string;
  status?: string;
  health?: number;
  orgId?: string;
  factors?: Record<string, number | string | boolean>;
}

interface Topology {
  organizations?: Organization[];
  networks?: Network[];
  total_count?: number;
  page?: number;
  page_size?: number;
  total_pages?: number;
}

interface AuditLog {
  id: string | number;
  timestamp: string;
  action: string;
  username: string;
  target_resource: string;
}

interface IsolationEvent {
  id: number;
  triggered_at: string;
  circuit_break_active: boolean | null;
}

// Returns the earliest Date that falls within the selected time range
function timeRangeStart(key: TimeRangeKey): Date {
  const now = new Date()
  switch (key) {
    case 'today':      { const d = new Date(now); d.setHours(0,0,0,0); return d }
    case 'past7days':  return new Date(now.getTime() - 7  * 86400_000)
    case 'past15days': return new Date(now.getTime() - 15 * 86400_000)
    case 'pastMonth':  return new Date(now.getTime() - 30 * 86400_000)
    case 'pastYear':   return new Date(now.getTime() - 365 * 86400_000)
  }
}

interface Organization {
  id: string;
  name: string;
}

interface DashboardData {
  globalHealth?: GlobalHealth;
  activeIncidents?: ActiveIncidents;
  topology?: Topology;
  auditLogs?: AuditLog[];
}

// Stable keys for time range - used for API and state (language-independent)
type TimeRangeKey = 'today' | 'past7days' | 'past15days' | 'pastMonth' | 'pastYear'

const TIME_RANGE_TO_API: Record<TimeRangeKey, string> = {
  today: 'Today',
  past7days: 'Past 7 days',
  past15days: 'Past 15 days',
  pastMonth: 'Past Month',
  pastYear: 'Past Year',
}

export default function DashboardPage() {
  const { data: session } = useSession()
  const { t } = useLanguage()
  const { lastStatusEvent } = useCircuitBreaker()
  const [data, setData] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [timeRangeKey, setTimeRangeKey] = useState<TimeRangeKey>('pastYear')
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [pageSize] = useState<number>(10)
  const [selectedNetworkIds, setSelectedNetworkIds] = useState<string[]>([])
  const [filterLocked, setFilterLocked] = useState(false)
  const [allNetworks, setAllNetworks] = useState<Network[]>([])
  // Source filter: all / noc / soc
  const [sourceFilter, setSourceFilter] = useState<'all' | 'noc' | 'soc'>('all')
  // SOC data for combined counts
  const [cbCommands, setCbCommands] = useState<IsolationEvent[]>([])
  const [suspEvents, setSuspEvents] = useState<SuspensionEvent[]>([])
  // Incremented each time a webhook event arrives to trigger immediate re-fetches
  const [refreshKey, setRefreshKey] = useState<number>(0)

  // Fetch all networks once (unpaginated) for filter dropdowns
  useEffect(() => {
    apiClient('/api/dashboard/networks')
      .then((nets: Network[]) => setAllNetworks(nets))
      .catch((err) => console.error('Failed to load all networks:', err))
  }, [])

  // Load persisted filter on mount
  useEffect(() => {
    const STORAGE_KEY = 'noc-dashboard-alert-network-filter'
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const parsed = JSON.parse(stored)
        if (parsed.locked) {
          setFilterLocked(true)
          setSelectedNetworkIds(parsed.networkIds || [])
        }
      }
    } catch (err) {
      console.error('Failed to load filter from localStorage:', err)
    }
  }, [])

  // -------------------------------------------------------------------------
  // Real-time webhook events via SSE
  // -------------------------------------------------------------------------
  const handleWebhookEvent = useCallback((event: WebhookEvent) => {
    if (event.type === 'alert_auto_resolved') {
      const downtimeNote = event.downtime ? ` — was down for ${event.downtime}` : ''
      toast.success(
        `${event.alert_type} on ${event.device_name} (${event.network_name})${downtimeNote}`,
        { duration: 8000 }
      )
    } else if (event.type === 'alert_created') {
      const priorityLabel = event.priority === 'critical' ? 'Critical' : event.priority === 'warning' ? 'Warning' : 'Info'
      toast(
        `[${priorityLabel}] ${event.alert_type} — ${event.device_name} (${event.network_name})`,
        {
          duration: 8000,
          icon: event.priority === 'critical' ? '🔴' : event.priority === 'warning' ? '🟡' : '🔵',
        }
      )
    }
    // Trigger immediate dashboard data re-fetch
    setRefreshKey((k) => k + 1)
  }, [])

  useWebhookEvents(handleWebhookEvent)

  // Re-fetch dashboard data when a suspension/resume SSE event arrives
  // so that KPI counts and suspension event lists stay current.
  useEffect(() => {
    if (!lastStatusEvent) return
    const evtType = lastStatusEvent.type
    if (evtType === 'cisco_ai_defense_block' || evtType === 'agents_suspended' || evtType === 'agents_resumed') {
      setRefreshKey((k) => k + 1)
    }
  }, [lastStatusEvent])

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Build query params
        const timeRangeApi = TIME_RANGE_TO_API[timeRangeKey]
        let url = `/api/dashboard/data?page=${currentPage}&page_size=${pageSize}&time_range=${encodeURIComponent(timeRangeApi)}`
        
        // Add network_ids filter if any networks selected
        if (selectedNetworkIds.length > 0) {
          url += `&network_ids=${encodeURIComponent(selectedNetworkIds.join(','))}`
        }
        
        const [dashboardData, cbData, suspData] = await Promise.all([
          apiClient(url),
          socApi.get('/api/soc/isolation-history?limit=200').catch(() => []),
          suspensionApi.list(200).catch(() => []),
        ])
        console.log('[Dashboard] Dashboard data fetched:', dashboardData)
        
        setData(dashboardData)

        // Filter SOC events to the selected time range
        const start = timeRangeStart(timeRangeKey)
        setCbCommands(Array.isArray(cbData) ? cbData.filter((e: IsolationEvent) => new Date(e.triggered_at) >= start) : [])
        setSuspEvents(Array.isArray(suspData) ? suspData.filter((e: SuspensionEvent) => new Date(e.triggered_at) >= start) : [])
      } catch (error) {
        console.error("Error fetching data", error)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [currentPage, pageSize, timeRangeKey, selectedNetworkIds, refreshKey])


  const handleExport = async () => {
    try {
      toast.loading(t.dashboard.preparingExport)
      
      const exportData = {
        exportedAt: new Date().toISOString(),
        timeRange: TIME_RANGE_TO_API[timeRangeKey],
        globalHealth: data?.globalHealth,
        activeIncidents: data?.activeIncidents,
        networks: data?.topology?.networks
      }
      
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `dashboard-export-${new Date().toISOString().split('T')[0]}.json`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      
      toast.dismiss()
      toast.success(t.dashboard.exportSuccess)
    } catch (error) {
      toast.dismiss()
      toast.error(t.dashboard.exportError)
      console.error('Export error:', error)
    }
  }

  const handleTimeRangeChange = (key: TimeRangeKey) => {
    setTimeRangeKey(key)
    const label = t.dashboard.timeRanges[key]
    toast.loading(`${t.dashboard.fetchingData} ${label}`)
    toast.dismiss()
    toast.success(`${t.dashboard.updatedTo} ${label}`)
  }

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage)
    // Data will be refetched automatically via useEffect
  }

  const handleNetworkFilterChange = (networkIds: string[]) => {
    setSelectedNetworkIds(networkIds)
    setCurrentPage(1) // Reset to first page when filter changes
    
    // Persist to localStorage if locked
    if (filterLocked) {
      const STORAGE_KEY = 'noc-dashboard-alert-network-filter'
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        locked: true,
        networkIds
      }))
    }
  }

  const handleLockToggle = () => {
    const newLocked = !filterLocked
    setFilterLocked(newLocked)
    
    const STORAGE_KEY = 'noc-dashboard-alert-network-filter'
    if (newLocked) {
      // Save current filter state
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        locked: true,
        networkIds: selectedNetworkIds
      }))
      toast.success(t.kpi.lockFilter)
    } else {
      // Clear lock
      localStorage.removeItem(STORAGE_KEY)
      toast.success(t.kpi.unlockFilter)
    }
  }

  if (loading) return <div className="h-full flex items-center justify-center"><Spinner className="h-12 w-12 text-brand-mid"/></div>

  // NOC counts (from backend)
  const nocHigh     = data?.activeIncidents?.high ?? 0
  const nocMedium   = data?.activeIncidents?.medium ?? 0
  const nocResolved = data?.activeIncidents?.resolved ?? 0

  // SOC counts (derived from filtered isolation / suspension events)
  // SOC: one "open" bucket (high) = active circuit break + active suspension; no separate medium.
  const socCbActive = cbCommands.filter(e => e.circuit_break_active !== false).length
  const socSuspActive = suspEvents.filter(e => e.action === 'SUSPENDED' && !e.resumed_at).length
  const socHigh = socCbActive + socSuspActive
  const socMedium = 0
  const socResolved =
    cbCommands.filter(e => e.circuit_break_active === false).length +
    suspEvents.filter(e => !!e.resumed_at).length

  const kpiData = {
    high:     sourceFilter === 'noc' ? nocHigh     : sourceFilter === 'soc' ? socHigh     : nocHigh + socHigh,
    medium:   sourceFilter === 'noc' ? nocMedium   : sourceFilter === 'soc' ? socMedium   : nocMedium + socMedium,
    resolved: sourceFilter === 'noc' ? nocResolved : sourceFilter === 'soc' ? socResolved : nocResolved + socResolved,
    alert_summary:      sourceFilter !== 'soc' ? data?.activeIncidents?.alert_summary      : undefined,
    alert_summary_data: sourceFilter !== 'soc' ? data?.activeIncidents?.alert_summary_data : undefined,
    by_network:         sourceFilter !== 'soc' ? data?.activeIncidents?.by_network         : undefined,
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] pr-5 pb-5 flex flex-col gap-5">
      
      {/* Main Grid: Left content (8 cols) + Ask NOC on right (4 cols) */}
      <div className="grid grid-cols-12 gap-5">
        {/* Left Column: Good Morning + Network sections */}
        <div className="col-span-8 space-y-5">
          {/* ROW 1: Greeting Header with Time Range & Export */}
          <div className="bg-white rounded-xl border border-[#DDD] p-6">
            <div className="flex justify-between items-start">
              <div className="space-y-1">
                <h1 className="text-2xl font-semibold text-[#0E172B]">
                  {t.dashboard.greeting}, {session?.user?.name?.split(' ')[0] || 'Admin'}
                </h1>
                <p className="text-base text-[#939393]">
                  {t.dashboard.overviewText}
                </p>
              </div>
              <div className="flex gap-3">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="bg-[#F5F5F5] border-0 text-[#676767] hover:bg-[#EFEFEF]"
                    >
                      {sourceFilter === 'all' ? t.kpi.sourceAll : sourceFilter === 'noc' ? t.kpi.sourceNoc : t.kpi.sourceSoc} <ChevronDown className="ml-2 h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setSourceFilter('all')}>
                      {t.kpi.sourceAll}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setSourceFilter('noc')}>
                      {t.kpi.sourceNoc}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setSourceFilter('soc')}>
                      {t.kpi.sourceSoc}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="bg-[#F5F5F5] border-0 text-[#676767] hover:bg-[#EFEFEF]"
                    >
                      {t.dashboard.timeRanges[timeRangeKey]} <ChevronDown className="ml-2 h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleTimeRangeChange('today')}>
                      {t.dashboard.timeRanges.today}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleTimeRangeChange('past7days')}>
                      {t.dashboard.timeRanges.past7days}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleTimeRangeChange('past15days')}>
                      {t.dashboard.timeRanges.past15days}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleTimeRangeChange('pastMonth')}>
                      {t.dashboard.timeRanges.pastMonth}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleTimeRangeChange('pastYear')}>
                      {t.dashboard.timeRanges.pastYear}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button 
                  variant="outline" 
                  className="bg-[#F5F5F5] border-0 text-[#676767] hover:bg-[#EFEFEF]"
                  onClick={handleExport}
                >
                  <Download className="mr-2 h-4 w-4" /> {t.dashboard.export}
                </Button>
              </div>
            </div>

            {/* KPI Cards within header */}
            <div className="mt-6">
              <KpiStats 
                data={kpiData} 
                hideMedium={sourceFilter === 'soc'}
                networks={allNetworks}
                selectedNetworkIds={selectedNetworkIds}
                onNetworkFilterChange={handleNetworkFilterChange}
                filterLocked={filterLocked}
                onLockToggle={handleLockToggle}
              />
            </div>

            {/* NOC Agent Status Card */}
            <div className="mt-4">
              <NocAgentStatusCard />
            </div>
          </div>

          {/* ROW 2: Network Health Score & Global Network Health */}
          <div className="grid grid-cols-12 gap-5">
            {/* Left: Network Health Score */}
            <div className="col-span-6">
              <NetworkHealthList 
                networks={data?.topology?.networks || []} 
                allNetworks={allNetworks}
                totalCount={data?.topology?.total_count || 0}
                currentPage={currentPage}
                totalPages={data?.topology?.total_pages || 1}
                onPageChange={handlePageChange}
              />
            </div>
            
            {/* Right: Global Health Donut */}
            <div className="col-span-6">
              <GlobalHealthDonut
                refreshKey={refreshKey}
                organizations={data?.topology?.organizations || []}
                initialData={data?.globalHealth as GlobalHealthData | undefined}
              />
            </div>
          </div>
        </div>

        {/* Right Column: Ask NOC - stretches full height */}
        <div className="col-span-4 overflow-hidden rounded-xl h-full">
          <AskNocManager mode="widget" />
        </div>
      </div>

      {/* ROW 3: Resource Monitoring & Performance/Wireless Cards */}
      <div className="grid grid-cols-12 gap-5">
        {/* Left: Resource Monitoring (5 cols) */}
        <div className="col-span-5">
          <ResourceMonitoringWidget />
        </div>

        {/* Right: Performance & Analytics (7 cols) */}
        <div className="col-span-7 space-y-5">
          {/* Organization-Wide Metrics: Network Uptime & Active Clients */}
          <OrganizationMetrics networks={allNetworks} />

          {/* Client Top Talkers — real bandwidth per client (last 24h) */}
          <ClientTopTalkersWidget />

          {/* Device Fleet — live status breakdown by product family */}
          <DeviceFleetWidget />
        </div>
      </div>


    </div>
  )
}
